import React from 'react';
import katex from 'katex';

interface MathRendererProps {
  content: string;
  className?: string;
}

export default function MathRenderer({ content, className = '' }: MathRendererProps) {
  // Safety check: ensure content is a string
  if (typeof content !== 'string') {
    console.warn('MathRenderer received non-string content:', content);
    return <div className={className}>Invalid content</div>;
  }

  const renderMathContent = (text: string) => {
    // Regex patterns for different math delimiters
    // Order matters: check $$ before $ to avoid false matches
    // We manually check for backslash before processing, simpler than lookbehind
    const dollarDisplayMathRegex = /\$\$(.+?)\$\$/g;  // $$...$$ for display math
    const dollarInlineMathRegex = /\$(.+?)\$/g;  // $...$ for inline math
    const displayMathRegex = /\\\[(.+?)\\\]/g;  // \[...\] for display math
    const inlineMathRegex = /\\\((.+?)\\\)/g;  // \(...\) for inline math

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let partIndex = 0;

    // Collect all math matches with their types
    let match: RegExpExecArray | null;
    const allMatches: Array<{ start: number; end: number; content: string; isDisplay: boolean }> = [];

    // 1. Handle $$...$$ (display math) - highest priority
    dollarDisplayMathRegex.lastIndex = 0;
    while ((match = dollarDisplayMathRegex.exec(text)) !== null) {
      // Skip if escaped (preceded by single backslash or double backslash)
      if (match.index > 0 && (text[match.index - 1] === '\\')) {
        continue;
      }
      if (match.index > 1 && text.substring(match.index - 2, match.index) === '\\\\') {
        continue;
      }
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        content: match[1],
        isDisplay: true
      });
    }

    // 2. Handle \[...\] (display math)
    displayMathRegex.lastIndex = 0;
    while ((match = displayMathRegex.exec(text)) !== null) {
      // Check if this overlaps with existing matches
      const overlaps = allMatches.some(m =>
        (match!.index >= m.start && match!.index < m.end) ||
        (match!.index + match![0].length > m.start && match!.index + match![0].length <= m.end)
      );
      if (!overlaps) {
        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1],
          isDisplay: true
        });
      }
    }

    // 3. Handle $...$ (inline math)
    dollarInlineMathRegex.lastIndex = 0;
    while ((match = dollarInlineMathRegex.exec(text)) !== null) {
      // Skip if escaped (preceded by single backslash or double backslash)
      if (match.index > 0 && (text[match.index - 1] === '\\')) {
        continue;
      }
      if (match.index > 1 && text.substring(match.index - 2, match.index) === '\\\\') {
        continue;
      }

      // Check if this overlaps with existing matches
      const overlaps = allMatches.some(m =>
        (match!.index >= m.start && match!.index < m.end) ||
        (match!.index + match![0].length > m.start && match!.index + match![0].length <= m.end)
      );

      if (!overlaps) {
        // Check if this should be display math (has \frac, \sum, etc. or is long)
        const shouldBeDisplay = /\\(frac|sum|int|prod|lim|sqrt|begin)/.test(match[1]) ||
                                match[1].length > 30;

        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1],
          isDisplay: shouldBeDisplay
        });
      }
    }

    // 4. Handle \(...\) (inline math)
    inlineMathRegex.lastIndex = 0;
    while ((match = inlineMathRegex.exec(text)) !== null) {
      // Check if this overlaps with existing matches
      const overlaps = allMatches.some(m =>
        (match!.index >= m.start && match!.index < m.end) ||
        (match!.index + match![0].length > m.start && match!.index + match![0].length <= m.end)
      );
      if (!overlaps) {
        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1],
          isDisplay: false
        });
      }
    }

    // Sort all matches by position
    allMatches.sort((a, b) => a.start - b.start);

    // Process the text with math expressions
    allMatches.forEach((mathMatch) => {
      // Add text before this math expression
      if (mathMatch.start > lastIndex) {
        const textBefore = text.slice(lastIndex, mathMatch.start);
        if (textBefore) {
          // Replace escaped dollar signs \\$ or \$ with just $
          const processedText = textBefore.replace(/\\\\?\$/g, '$');
          // Split by newlines and create proper line breaks
          const lines = processedText.split('\n');
          lines.forEach((line, index) => {
            if (line) {
              parts.push(
                <span key={`text-${partIndex++}`}>
                  {line}
                </span>
              );
            }
            if (index < lines.length - 1) {
              parts.push(<br key={`br-${partIndex++}`} />);
            }
          });
        }
      }

      // Render the math expression
      try {
        const html = katex.renderToString(mathMatch.content, {
          displayMode: mathMatch.isDisplay,
          throwOnError: false,
          errorColor: '#cc0000',
          strict: false
        });

        parts.push(
          <span
            key={`math-${partIndex++}`}
            dangerouslySetInnerHTML={{ __html: html }}
            className={mathMatch.isDisplay ? 'block text-center my-2' : 'inline'}
          />
        );
      } catch (error) {
        // Fallback: show the original LaTeX if rendering fails
        parts.push(
          <span key={`error-${partIndex++}`} className="text-red-400 font-mono">
            {mathMatch.isDisplay ? `$$${mathMatch.content}$$` : `$${mathMatch.content}$`}
          </span>
        );
      }

      lastIndex = mathMatch.end;
    });

    // Add any remaining text
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        // Replace escaped dollar signs \\$ or \$ with just $
        const processedText = remainingText.replace(/\\\\?\$/g, '$');
        // Split by newlines and create proper line breaks
        const lines = processedText.split('\n');
        lines.forEach((line, index) => {
          if (line) {
            parts.push(
              <span key={`text-${partIndex++}`}>
                {line}
              </span>
            );
          }
          if (index < lines.length - 1) {
            parts.push(<br key={`br-${partIndex++}`} />);
          }
        });
      }
    }

    return parts.length > 0 ? parts : [text];
  };

  // If no math expressions found, return the content with proper line breaks
  if (!content.includes('\\[') && !content.includes('\\(') && !content.includes('$')) {
    const lines = content.split('\n');
    return (
      <div className={className}>
        {lines.map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      {renderMathContent(content)}
    </div>
  );
}
